import React, { useState } from 'react';
import axios from 'axios';
import { Mail, Loader2, CheckCircle2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const T = {
  eyebrow: { en: 'Subscribe to BMP Insights', pt: 'Subscrever BMP Insights', fr: 'Abonnez-vous à BMP Insights', ar: 'اشترك في BMP Insights', zh: '订阅 BMP 资讯' },
  heading: { en: "Get next month's playbook, before your competitors.", pt: 'Receba o guia do próximo mês, antes dos seus concorrentes.', fr: 'Recevez le guide du mois prochain, avant vos concurrents.', ar: 'احصل على دليل الشهر المقبل قبل منافسيك.', zh: '抢在竞争对手之前，获取下月的行动指南。' },
  desc: { en: 'Curated insights on UAE compliance, procurement digitalisation and cross-border trade — written by our team, delivered straight to your inbox. No noise.', pt: 'Análises selecionadas sobre conformidade nos EAU, digitalização de compras e comércio transfronteiriço — escritas pela nossa equipa, entregues diretamente na sua caixa de entrada. Sem ruído.', fr: 'Analyses sélectionnées sur la conformité aux EAU, la digitalisation des achats et le commerce transfrontalier — rédigées par notre équipe, livrées directement dans votre boîte de réception. Sans bruit.', ar: 'رؤى منتقاة حول الامتثال في الإمارات ورقمنة المشتريات والتجارة عبر الحدود — بقلم فريقنا، تصل مباشرة إلى بريدك. بلا ضجيج.', zh: '精选的阿联酋合规、采购数字化与跨境贸易洞察 —— 由我们团队撰写，直接送达您的收件箱，绝无干扰。' },
  namePlaceholder: { en: 'Your name (optional)', pt: 'O seu nome (opcional)', fr: 'Votre nom (facultatif)', ar: 'اسمك (اختياري)', zh: '您的姓名（选填）' },
  sending: { en: 'Sending…', pt: 'A enviar…', fr: 'Envoi…', ar: 'جارٍ الإرسال…', zh: '发送中…' },
  subscribed: { en: 'Subscribed', pt: 'Subscrito', fr: 'Abonné', ar: 'تم الاشتراك', zh: '已订阅' },
  subscribe: { en: 'Subscribe', pt: 'Subscrever', fr: "S'abonner", ar: 'اشترك', zh: '订阅' },
  success: { en: 'Thanks! A welcome email is on its way.', pt: 'Obrigado! Um e-mail de boas-vindas está a caminho.', fr: 'Merci ! Un e-mail de bienvenue arrive.', ar: 'شكرًا! رسالة ترحيب في طريقها إليك.', zh: '感谢！欢迎邮件即将送达。' },
  disclaimer: { en: 'We respect your inbox. Unsubscribe any time. GDPR & UAE DPL compliant.', pt: 'Respeitamos a sua caixa de entrada. Cancele a qualquer momento. Em conformidade com RGPD e UAE DPL.', fr: 'Nous respectons votre boîte de réception. Désabonnez-vous à tout moment. Conforme RGPD et UAE DPL.', ar: 'نحترم بريدك. يمكنك إلغاء الاشتراك في أي وقت. متوافق مع GDPR وقانون حماية البيانات الإماراتي.', zh: '我们尊重您的收件箱，可随时取消订阅。符合 GDPR 与阿联酋 DPL 规定。' },
  emailRequired: { en: 'Please enter your email.', pt: 'Por favor, introduza o seu e-mail.', fr: 'Veuillez saisir votre e-mail.', ar: 'يرجى إدخال بريدك الإلكتروني.', zh: '请输入您的电子邮箱。' },
  failed: { en: 'Subscription failed. Please try again.', pt: 'A subscrição falhou. Tente novamente.', fr: "L'abonnement a échoué. Veuillez réessayer.", ar: 'فشل الاشتراك. يرجى المحاولة مرة أخرى.', zh: '订阅失败，请重试。' },
};

/**
 * Subscribe to Insights — newsletter capture CTA.
 * Posts to /api/newsletter/subscribe with the currently viewed article's slug
 * and language so we can attribute the lead to the right content piece.
 */
const SubscribeInsights = ({ articleSlug, language = 'en' }) => {
  const L = (k) => (T[k] && (T[k][language] || T[k].en)) || '';
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState('');

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    if (!email.trim()) { setError(L('emailRequired')); return; }
    setLoading(true);
    try {
      const r = await axios.post(`${API_URL}/api/newsletter/subscribe`, {
        email: email.trim(),
        name: name.trim() || null,
        source: 'insights',
        article_slug: articleSlug || null,
        language,
      });
      setDone(true);
      setEmail(''); setName('');
      setTimeout(() => setDone(false), 6000);
      if (r.data?.already_subscribed) {
        // Still show success — message is friendly either way
      }
    } catch (err) {
      setError(err.response?.data?.detail || L('failed'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      className="relative bg-gradient-to-br from-[#023E8A] via-[#0353A4] to-[#001219] py-16 md:py-20 overflow-hidden"
      data-testid="subscribe-insights"
    >
      {/* decorative pattern */}
      <div className="absolute inset-0 opacity-[0.07]" style={{
        backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)',
        backgroundSize: '32px 32px',
      }} />

      <div className="container mx-auto px-6 relative z-10">
        <div className="max-w-3xl mx-auto text-center text-white">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/10 border border-white/20 backdrop-blur-sm mb-5">
            <Mail className="w-4 h-4 text-[#48CAE4]" />
            <span className="text-xs font-semibold uppercase tracking-wider text-[#48CAE4]">
              {L('eyebrow')}
            </span>
          </div>
          <h3 className="text-2xl md:text-3xl lg:text-4xl font-extrabold mb-3 leading-tight">
            {L('heading')}
          </h3>
          <p className="text-white/75 max-w-xl mx-auto mb-8 text-base md:text-lg">
            {L('desc')}
          </p>

          <form
            onSubmit={submit}
            className="grid grid-cols-1 md:grid-cols-[1fr_1.3fr_auto] gap-3 max-w-2xl mx-auto"
          >
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={L('namePlaceholder')}
              disabled={loading || done}
              className="px-4 py-3 rounded-lg bg-white/10 border border-white/20 text-white placeholder:text-white/50 focus:outline-none focus:border-[#48CAE4] focus:bg-white/15 backdrop-blur transition"
              data-testid="subscribe-name"
            />
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="your.email@company.com"
              required
              disabled={loading || done}
              className="px-4 py-3 rounded-lg bg-white/10 border border-white/20 text-white placeholder:text-white/50 focus:outline-none focus:border-[#48CAE4] focus:bg-white/15 backdrop-blur transition"
              data-testid="subscribe-email"
            />
            <button
              type="submit"
              disabled={loading || done}
              className="px-6 py-3 rounded-lg bg-[#FF6B35] hover:bg-[#E5562D] text-white font-bold transition-all hover:scale-105 disabled:opacity-60 disabled:hover:scale-100 flex items-center justify-center gap-2 min-w-[140px]"
              data-testid="subscribe-btn"
            >
              {loading ? (
                <><Loader2 className="w-4 h-4 animate-spin" /> {L('sending')}</>
              ) : done ? (
                <><CheckCircle2 className="w-5 h-5" /> {L('subscribed')}</>
              ) : (
                <>{L('subscribe')}</>
              )}
            </button>
          </form>

          {error && <p className="text-red-300 text-sm mt-4" data-testid="subscribe-error">{error}</p>}
          {done && (
            <p className="text-emerald-300 text-sm mt-4" data-testid="subscribe-success">
              {L('success')}
            </p>
          )}
          <p className="text-xs text-white/50 mt-5">
            {L('disclaimer')}
          </p>
        </div>
      </div>
    </section>
  );
};

export default SubscribeInsights;
